import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import specialistAuthService from '../services/specialistAuthService';

const SpecialistAuthContext = createContext(null);

export const SpecialistAuthProvider = ({ children }) => {
  const [specialist, setSpecialist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if specialist is authenticated on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('specialist_token');
      const storedSpecialist = localStorage.getItem('specialist');
      
      if (token && storedSpecialist) {
        try {
          const parsed = JSON.parse(storedSpecialist);
          setSpecialist(parsed);
          
          // Validate token in background
          const response = await specialistAuthService.validateToken();
          if (response.valid) {
            setSpecialist(response.specialist);
            localStorage.setItem('specialist', JSON.stringify(response.specialist));
          }
        } catch (err) {
          console.error('Specialist token validation error:', err);
          // Keep logged in with localStorage data
          const data = localStorage.getItem('specialist');
          if (data) {
            setSpecialist(JSON.parse(data));
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password, rememberMe = false) => {
    try {
      setError(null);
      const response = await specialistAuthService.login(email, password, rememberMe);
      
      if (response.token) {
        // Clear any existing regular user session to prevent cross-auth contamination
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        localStorage.setItem('specialist_token', response.token);
        localStorage.setItem('specialist', JSON.stringify(response.specialist));
        setSpecialist(response.specialist);
        return { success: true, specialist: response.specialist };
      }
      
      return { success: false, error: response.error || 'Login failed' };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Login failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const register = async (data) => {
    try {
      setError(null);
      const response = await specialistAuthService.register(data);
      
      if (response.token) {
        localStorage.setItem('specialist_token', response.token);
        localStorage.setItem('specialist', JSON.stringify(response.specialist));
        setSpecialist(response.specialist);
        return { success: true, specialist: response.specialist };
      }
      
      return { success: false, error: response.error || 'Registration failed' };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Registration failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = useCallback(async () => {
    // Clear ALL auth data to prevent session leakage
    localStorage.removeItem('specialist_token');
    localStorage.removeItem('specialist');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setSpecialist(null);
  }, []);

  const updateProfile = async (profileData) => {
    try {
      setError(null);
      const response = await specialistAuthService.updateProfile(profileData);
      
      if (response.specialist) {
        localStorage.setItem('specialist', JSON.stringify(response.specialist));
        setSpecialist(response.specialist);
        return { success: true, specialist: response.specialist };
      }
      
      return { success: false, error: response.error || 'Profile update failed' };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Profile update failed.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const uploadAvatar = async (file) => {
    try {
      setError(null);
      const response = await specialistAuthService.uploadAvatar(file);
      if (response.specialist) {
        localStorage.setItem('specialist', JSON.stringify(response.specialist));
        setSpecialist(response.specialist);
        return { success: true, specialist: response.specialist };
      }
      return { success: false, error: response.error || 'Avatar upload failed' };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Avatar upload failed.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const refreshSpecialist = (newData) => {
    if (newData) {
      setSpecialist(newData);
      try {
        localStorage.setItem('specialist', JSON.stringify(newData));
      } catch (e) {
        console.warn('Could not save specialist to localStorage:', e);
      }
    } else {
      const stored = localStorage.getItem('specialist');
      if (stored) {
        setSpecialist(JSON.parse(stored));
      }
    }
  };

  const clearError = () => setError(null);

  const value = {
    specialist,
    loading,
    error,
    isAuthenticated: !!specialist,
    login,
    register,
    logout,
    updateProfile,
    uploadAvatar,
    refreshSpecialist,
    clearError
  };

  return (
    <SpecialistAuthContext.Provider value={value}>
      {children}
    </SpecialistAuthContext.Provider>
  );
};

export const useSpecialistAuth = () => {
  const context = useContext(SpecialistAuthContext);
  if (!context) {
    throw new Error('useSpecialistAuth must be used within a SpecialistAuthProvider');
  }
  return context;
};

export default SpecialistAuthContext;
